/**
 * Dutchie API Integration
 * This file handles all interactions with the Dutchie API for product data and cart management
 */

import { areTagsEquivalent } from "../format-text";

// Dutchie retailer ID
const RETAILER_ID = "94626601-56a5-4764-a8ef-be47fa946bae"

// Types for Dutchie products from the API
export interface DutchieProduct {
  id: string
  name: string
  description?: string
  category: string
  subcategory?: string
  strainType?: string // Accept any string value including 'NOT_APPLICABLE'
  effects?: string[]
  tags?: string[]
  brand?: {
    id: string
    name: string
  }
  variants: DutchieVariant[]
  image?: string
  images?: {
    url: string
    label?: string
  }[]
  potencyThc?: {
    formatted: string
    range: [number, number]
    unit: string
  }
  potencyCbd?: {
    formatted: string
    range: [number, number]
    unit: string
  }
  staffPick?: boolean
}

export interface DutchieVariant {
  id: string
  option: string
  priceRec: number
  priceMed?: number
  specialPriceRec?: number
  quantity: number
}

// Simplified product type for the frontend
export interface ProductDisplay {
  id: string
  variantId: string
  name: string
  option?: string
  fullName: string
  category: string
  subcategory?: string
  brand: string
  image: string
  price: number
  specialPrice?: number
  thc?: string
  thcRange?: [number, number]
  cbd?: string
  cbdRange?: [number, number]
  description?: string
  effects?: string[]
  terpenes?: string[]
  // Structured lab data from Dutchie (name + % value), when available.
  terpeneProfile?: { name: string; value: number; unit?: string }[]
  cannabinoidProfile?: { name: string; value: number; unit?: string }[]
  // Allow more strain type values while maintaining backward compatibility
  strainType?: string
  inStock: boolean
  staffPick: boolean
  // Collection tags to enable dynamic collections
  collections: string[]
  // Rating (if available from Dutchie)
  rating?: number
  
  // Additional properties for enhanced filtering
  variants?: {
    id: string;
    option: string;
    priceRec: number;
    priceMed?: number;
    specialPriceRec?: number;
    quantity: number;
  }[];
  
  // Potency data from API
  potencyThc?: {
    formatted: string;
    range: number[];
    unit: string;
  };
  potencyCbd?: {
    formatted: string;
    range: number[];
    unit: string;
  };
  
  // Properties added by enhanceProducts
  isInfused?: boolean;
  isPack?: boolean;
  isSingle?: boolean;
}

// Types for Dutchie cart
export interface DutchieCartItem {
  productId: string
  variantId: string
  quantity: number
  price: number
  name: string
  image?: string
  option?: string
}

export interface DutchieCart {
  items: DutchieCartItem[]
  subtotal: number
  tax?: number
  total: number
}

// Cache configuration
const CACHE_DURATION = 60 * 15 // 15 minutes in seconds
let productsCache: {
  data: ProductDisplay[] | null
  timestamp: number
} = {
  data: null,
  timestamp: 0,
}

/**
 * Fetches products from API endpoint with proper fallback handling
 */
export async function getProducts(): Promise<ProductDisplay[]> {
  const now = Math.floor(Date.now() / 1000) // Current time in seconds

  // Return cached data if available and not expired
  if (productsCache.data && now - productsCache.timestamp < CACHE_DURATION) {
    console.log('Returning cached products data')
    return productsCache.data
  }

  try {
    console.log("Fetching products from API...")
    
    // Use relative URL for both client and server-side to avoid port mismatch
    const baseUrl = typeof window === 'undefined' 
      ? `http://localhost:${process.env.PORT || 3002}` // Server-side
      : ''; // Client-side - use relative URL
    const apiUrl = `${baseUrl}/api/products`;
    
    console.log('API URL:', apiUrl);
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // Add timeout for client-side requests
      ...(typeof window !== 'undefined' && {
        signal: AbortSignal.timeout(10000) // 10 second timeout
      })
    });
    
    console.log('Response status:', response.status);
    
    if (!response.ok) {
      throw new Error(`API responded with status ${response.status}: ${response.statusText}`);
    }
    
    const result = await response.json();
    console.log('API result type:', Array.isArray(result) ? 'array' : typeof result);
    console.log('API result length:', Array.isArray(result) ? result.length : 'N/A');
    
    let products: DutchieProduct[]
    if (result.data && Array.isArray(result.data)) {
      products = result.data;
    } else if (Array.isArray(result)) {
      products = result; // Direct array response
    } else {
      throw new Error('Invalid API response format: expected array or {data: array}');
    }
    
    console.log(`Successfully fetched ${products.length} products from API`);

    // Transform products to display format (flatten variants)
    const displayProducts = transformProductsForDisplay(products)
    console.log(`Transformed to ${displayProducts.length} display products`);

    // Update cache
    productsCache = {
      data: displayProducts,
      timestamp: now,
    }

    return displayProducts
  } catch (fetchError) {
    console.error('Error fetching products from API:', fetchError);
    
    // Check if we have stale cached data to fall back to
    if (productsCache.data && productsCache.data.length > 0) {
      console.log('Returning stale cached data as fallback');
      return productsCache.data;
    }
    
    console.log("Returning mock data as fallback")
    
    // Fallback to mock data
    const products = getMockProducts();
    const displayProducts = transformProductsForDisplay(products)
    
    // Cache the mock data too
    productsCache = {
      data: displayProducts,
      timestamp: now,
    }
    
    return displayProducts
  }
}

/**
 * Helper function to extract collection tags from a Dutchie product
 * This allows for dynamic creation of collections based on product attributes
 */
function extractCollectionTags(product: DutchieProduct): string[] {
  const collections: string[] = [];
  
  // Add any explicit tags from the product
  if (product.tags && Array.isArray(product.tags)) {
    // Filter out any NOT_APPLICABLE tags
    collections.push(...product.tags.filter(tag => tag !== 'NOT_APPLICABLE'));
  }
  
  // Add implicit collections based on product properties
  if (product.staffPick) {
    collections.push('staff-pick');
  }
  
  // Check for sale items
  const hasVariantOnSale = product.variants.some(
    v => v.specialPriceRec !== undefined && v.specialPriceRec < v.priceRec
  );
  if (hasVariantOnSale) {
    collections.push('on-sale');
  }
  
  // Add collections based on strain type
  if (product.strainType && product.strainType !== 'NOT_APPLICABLE') {
    collections.push(`strain-${product.strainType.toLowerCase()}`);
  }
  
  // Add collections based on effects
  if (product.effects && Array.isArray(product.effects)) {
    product.effects
      .filter(effect => effect !== 'NOT_APPLICABLE')
      .forEach(effect => collections.push(`effect-${effect.toLowerCase()}`));
  }
  
  // Add collections based on high rating (if rating exists)
  if ((product as any).rating && (product as any).rating >= 4.5) {
    collections.push('top-rated');
  }
  
  return collections;
}

/**
 * Transform Dutchie API products to display format
 * This flattens variants into separate products for easier display
 */
/**
 * Normalizes Dutchie's ActiveTerpene / ActiveCannabinoid arrays into a simple
 * { name, value, unit } list: drops zero/blank entries and sorts descending so
 * the dominant compound is first. Returns undefined when there's nothing real.
 */
function mapActiveList(
  raw: any[] | undefined,
  key: "terpene" | "cannabinoid"
): { name: string; value: number; unit?: string }[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const list = raw
    .map((entry) => ({
      name: entry?.[key]?.name as string | undefined,
      value: typeof entry?.value === "number" ? entry.value : 0,
      unit: entry?.unitSymbol || undefined,
    }))
    .filter((e) => e.name && e.value > 0)
    .sort((a, b) => b.value - a.value) as { name: string; value: number; unit?: string }[];
  return list.length > 0 ? list : undefined;
}

function transformProductsForDisplay(products: DutchieProduct[]): ProductDisplay[] {
  const displayProducts: ProductDisplay[] = []

  if (!products || !Array.isArray(products)) {
    console.warn("Invalid products data:", products)
    return displayProducts
  }

  products.forEach((product) => {
    // Skip products with no variants
    if (!product.variants || product.variants.length === 0) return

    product.variants.forEach((variant) => {
      const displayProduct: ProductDisplay = {
        id: product.id,
        variantId: variant.id,
        name: product.name,
        option: variant.option || '',
        fullName: variant.option ? `${product.name} - ${variant.option}` : product.name,
        category: product.category,
        subcategory: product.subcategory,
        brand: product.brand?.name || 'Unknown',
        image: product.image || product.images?.[0]?.url || '',
        price: variant.priceRec,
        specialPrice: variant.specialPriceRec && variant.specialPriceRec < variant.priceRec ? variant.specialPriceRec : undefined,
        thc: product.potencyThc?.formatted,
        thcRange: product.potencyThc?.range,
        cbd: product.potencyCbd?.formatted,
        cbdRange: product.potencyCbd?.range,
        description: product.description,
        effects: product.effects && product.effects.length > 0 ? product.effects.filter(effect => effect !== 'NOT_APPLICABLE') : undefined,
        // Structured lab data: keep only entries with a real (> 0) value,
        // sorted high → low, so the UI can render a Dutchie-style ranked list.
        terpeneProfile: mapActiveList((product as any).terpenes, "terpene"),
        cannabinoidProfile: mapActiveList((product as any).cannabinoids, "cannabinoid"),
        // Keep valid strain types but filter out NOT_APPLICABLE
        strainType: (product.strainType && product.strainType !== 'NOT_APPLICABLE') ? product.strainType.toLowerCase() : undefined,
        inStock: variant.quantity > 0,
        staffPick: product.staffPick || false,
        // Extract collections from tags and other properties
        collections: extractCollectionTags(product),
        // Add rating if available
        rating: (product as any).rating,
      }

      displayProducts.push(displayProduct)
    })
  })

  return displayProducts
}

/**
 * Fetches a single product by ID
 */
export async function getProductById(id: string, variantId?: string): Promise<ProductDisplay | null> {
  const products = await getProducts()

  if (variantId) {
    // Find specific variant
    return products.find((product) => product.id === id && product.variantId === variantId) || null
  } else {
    // Find any variant of this product (first match)
    return products.find((product) => product.id === id) || null
  }
}

/**
 * Get staff picks products for featured displays
 */
export async function getStaffPicks(limit: number = 8): Promise<ProductDisplay[]> {
  const products = await getProducts()
  
  // Filter products marked as staff picks
  const staffPicks = products.filter(product => product.staffPick)
  
  // If not enough staff picks are available, fill with top products
  if (staffPicks.length < limit) {
    const remainingNeeded = limit - staffPicks.length
    const otherProducts = products
      .filter(p => !p.staffPick)
      .sort((a, b) => (b.rating || 0) - (a.rating || 0))
      .slice(0, remainingNeeded)
    
    return [...staffPicks, ...otherProducts]
  }
  
  // Return randomized staff picks limited to the requested number
  return staffPicks
    .sort(() => Math.random() - 0.5)
    .slice(0, limit)
}

/**
 * Get all products for a specific category
 * @param category The category name (e.g., 'flower', 'pre-rolls')
 * @param subcategory Optional subcategory to filter by
 */
export async function getProductsByCategory(category: string, subcategory?: string): Promise<ProductDisplay[]> {
  console.log(`Getting products for category: ${category}`);
  const products = await getProducts();
  
  // Normalize category name - handle both formats: 'PRE_ROLLS' from API and 'pre-rolls' from URL
  const categoryLower = category.toLowerCase().replace(/_/g, '-');
  
  // Debug logs to help diagnose issues
  console.log(`Filtering for category: ${category}, normalized as: ${categoryLower}`);
  console.log(`Total products before filtering: ${products.length}`);
  
  // Strict category match. Previous logic fell back to
  //   product.name.toLowerCase().includes(categoryLower)
  // which pulled Veterans Choice "Warrior Balm" (TOPICALS) and other
  // wrong-category items into /shop/category/tinctures whenever the
  // word "tincture" appeared anywhere in the product name. We use
  // areTagsEquivalent() instead — it already knows the legitimate
  // aliases (tinctures ↔ drops/oils, vapes ↔ vaporizers, etc.) without
  // the false positives.
  return products.filter((product) => {
    const productCategory = product.category.toLowerCase().replace(/_/g, '-');

    const categoryMatch =
      productCategory === categoryLower ||
      areTagsEquivalent(productCategory, categoryLower);

    // Subcategory filter only applies when explicitly requested
    const subcategoryMatch = subcategory
      ? product.subcategory?.toLowerCase() === subcategory.toLowerCase()
      : true;

    return categoryMatch && subcategoryMatch && product.inStock;
  });
}

/**
 * Get all products for a specific strain type
 * @param strainType The strain type ('sativa', 'indica', 'hybrid', etc.)
 * @param limit Optional limit on number of products returned
 */
export async function getProductsByStrainType(strainType: string, limit?: number): Promise<ProductDisplay[]> {
  const products = await getProducts()
  // First try to use the collection tag approach
  let result = products.filter((product) => 
    product.collections.includes(`strain-${strainType.toLowerCase()}`) && product.inStock
  )
  
  // If no results, fall back to the traditional filter
  if (result.length === 0) {
    result = products.filter((product) => 
      product.strainType?.toLowerCase() === strainType.toLowerCase() && product.inStock
    )
  }
  
  // Apply limit if specified
  if (limit && limit > 0) {
    result = result.slice(0, limit)
  }
  
  return result
}

/**
 * Get products by collection tag
 * @param collectionTag The collection tag to filter by (e.g., 'staff-pick', 'on-sale')
 * @param limit Optional limit on number of products returned
 */
export async function getProductsByCollection(collectionTag: string, limit?: number): Promise<ProductDisplay[]> {
  const products = await getProducts()
  let filtered = products.filter((product) => {
    // Special case for staff picks - check both the collections array and the staffPick boolean property
    if (collectionTag === 'staff-pick') {
      return (product.collections.includes(collectionTag) || product.staffPick) && product.inStock
    }
    // Special case for sale items - check both collections and directly for specialPrice < price
    else if (collectionTag === 'on-sale') {
      return (product.collections.includes(collectionTag) || 
             (product.specialPrice !== undefined && product.specialPrice < product.price)) && 
             product.inStock
    }
    // Default behavior for other collection tags
    else {
      return product.collections.includes(collectionTag) && product.inStock
    }
  })
  
  // Apply limit if specified
  if (limit && limit > 0) {
    filtered = filtered.slice(0, limit)
  }
  
  return filtered
}

/**
 * Get products on sale
 */
export async function getSaleItems(limit?: number): Promise<ProductDisplay[]> {
  return getProductsByCollection('on-sale', limit)
}

/**
 * Get top-rated products
 */
export async function getTopRatedProducts(limit?: number): Promise<ProductDisplay[]> {
  return getProductsByCollection('top-rated', limit)
}

// Note: getProductsByStrainType is already defined above with enhanced implementation

/**
 * Get products by effect
 * @param effect The effect to filter by (e.g., 'relaxed', 'energetic')
 */
export async function getProductsByEffect(effect: string, limit?: number): Promise<ProductDisplay[]> {
  return getProductsByCollection(`effect-${effect.toLowerCase()}`, limit)
}

/**
 * Get products by brand name
 * @param brandName The brand name to filter by
 * @param limit Optional limit on number of products returned
 */
export async function getProductsByBrand(brandName: string, limit?: number): Promise<ProductDisplay[]> {
  const products = await getProducts()
  
  // Format the input brand name for flexible matching
  // Remove hyphens and lowercase for consistent comparison
  const formattedBrandName = brandName.toLowerCase().replace(/-/g, ' ')
  
  console.log(`Looking for brand: ${formattedBrandName}`) // Debugging log
  
  // Find products matching this brand
  let filtered = products.filter((product) => {
    // Safely handle different brand formats
    let productBrand = ''
    
    if (typeof product.brand === 'string') {
      productBrand = product.brand.toLowerCase()
    } else if (product.brand && typeof product.brand === 'object') {
      const brandObj = product.brand as { name: string }
      if (brandObj.name) {
        productBrand = brandObj.name.toLowerCase()
      }
    }
    
    // More flexible matching strategy:
    // 1. Direct match
    // 2. Substring match
    // 3. Match after removing special characters
    const formattedProductBrand = productBrand.replace(/[^a-z0-9\s]/g, '')
    const formattedSearchBrand = formattedBrandName.replace(/[^a-z0-9\s]/g, '')
    
    const isMatch = 
      productBrand === formattedBrandName || // Exact match with formatted input
      productBrand.includes(formattedBrandName) || // Contains the formatted input
      formattedBrandName.includes(productBrand) || // Input contains the product brand
      formattedProductBrand.includes(formattedSearchBrand) || // Simplified string match
      formattedSearchBrand.includes(formattedProductBrand) // Simplified string match (reverse)
    
    if (isMatch) {
      console.log(`Match found: Product brand "${productBrand}" matches "${formattedBrandName}"`) // Debugging log
    }
    
    return isMatch && product.inStock
  })
  
  console.log(`Found ${filtered.length} products for brand ${formattedBrandName}`) // Debugging log
  
  // Apply limit if specified
  if (limit && limit > 0) {
    filtered = filtered.slice(0, limit)
  }
  
  return filtered
}

// Pricing summary from Dutchie checkout
export interface CheckoutPricing {
  subtotal: number;
  discounts: number;
  taxes: number;
  fees: number;
  total: number;
}

// Result from checkout creation
export interface CheckoutResult {
  url: string;
  pricing: CheckoutPricing | null;
}

/**
 * Creates a checkout via the Dutchie GraphQL API and returns the checkout URL and pricing
 * Uses our secure server-side API endpoint to keep API keys private
 * 
 * @param cart - The cart object containing items to checkout
 * @param fulfillmentType - Whether the order is for pickup or delivery
 * @returns Object with checkout URL and pricing summary (including discounts)
 */
export async function createDutchieCheckout(cart: DutchieCart, fulfillmentType: "pickup" | "delivery"): Promise<CheckoutResult> {
  // The location/retailer ID for 716 Cannabis
  const locationId = "94626601-56a5-4764-a8ef-be47fa946bae";
  
  try {
    // Use our secure server-side API endpoint to create the checkout
    // This keeps our Dutchie API key secure
    console.log("Creating checkout via server-side API endpoint...");
    
    // Get the base URL dynamically
    const baseApiUrl = typeof window !== 'undefined' 
      ? window.location.origin 
      : 'https://716cannabisllc.com';
    
    const endpoint = `${baseApiUrl}/api/dutchie-checkout`;
    console.log("Calling endpoint:", endpoint);
    
    // Call our server-side API endpoint
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        cart,
        fulfillmentType
      })
    });

    console.log("API response status:", response.status);
    
    // Check if we got a successful response
    if (response.ok) {
      const data = await response.json();
      console.log("API response data:", data);
      
      const url = data.checkoutUrl;
      const pricing = data.pricing || null;
      
      if (url) {
        console.log("Successfully created checkout URL via API:", url);
        if (pricing?.discounts > 0) {
          console.log(`💰 Bundle savings: $${pricing.discounts.toFixed(2)}`);
        }
        return { url, pricing };
      } else {
        console.warn("API did not return a checkout URL. Response:", data);
      }
    } else {
      console.error("API error:", response.status, response.statusText);
      const errorText = await response.text();
      console.error("Error details:", errorText);
    }
  } catch (error) {
    console.error("Error using server API for checkout:", error);
  }
  
  // Fallback to direct URL if API call fails
  console.log("Falling back to direct URL construction...");
  
  // Get or create a persistent checkout ID from cookies
  const checkoutId = getOrCreateCheckoutId();
  
  // Build the checkout URL manually
  const baseUrl = `https://checkout.716cannabisllc.com/checkouts/${locationId}/${checkoutId}`;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://716cannabisllc.com';
  const checkoutUrl = `${baseUrl}?fulfillment=${fulfillmentType}&r=${encodeURIComponent(origin)}`;
  
  // Set cookies for compatibility with the original site
  if (typeof window !== 'undefined') {
    document.cookie = `dutchie-checkout-id=${checkoutId}; path=/; max-age=${30*24*60*60}`;
    document.cookie = `dutchie-cart-total-item=${cart.items.length}; path=/; max-age=${30*24*60*60}`;
    document.cookie = `wp-cannasync-active-store=${locationId}; path=/; max-age=${30*24*60*60}`;
  }
  
  return { url: checkoutUrl, pricing: null };
}

// Backward compatible wrapper that just returns the URL
export async function createDutchieCheckoutUrl(cart: DutchieCart, fulfillmentType: "pickup" | "delivery"): Promise<string> {
  const result = await createDutchieCheckout(cart, fulfillmentType);
  return result.url;
}

/**
 * Creates a checkout session with the Dutchie GraphQL API
 * @param retailerId - The Dutchie retailer ID
 * @param fulfillmentType - Whether the order is for pickup or delivery
 * @returns The checkout session object with ID
 */
async function createDutchieCheckoutSession(retailerId: string, fulfillmentType: "pickup" | "delivery"): Promise<{id: string}> {
  try {
    // Convert fulfillment type to orderType for Dutchie API
    const orderType = fulfillmentType === "pickup" ? "PICKUP" : "DELIVERY";

    // Create the GraphQL mutation
    const mutation = `
      mutation CreateCheckout(
        $retailerId: ID!,
        $orderType: OrderType!,
        $pricingType: PricingType!
      ) {
        createCheckout(
          retailerId: $retailerId,
          orderType: $orderType,
          pricingType: $pricingType
        ) {
          id
          redirectUrl
        }
      }`;

    // Make the GraphQL request to Dutchie API
    const response = await fetch('https://dutchie.com/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        query: mutation,
        variables: {
          retailerId: retailerId,
          orderType: orderType,
          pricingType: "RECREATIONAL" // Hardcoded for now, could be made dynamic
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to create checkout: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Check if we have a valid checkout ID
    if (data?.data?.createCheckout?.id) {
      return {
        id: data.data.createCheckout.id
      };
    } else {
      console.error('Invalid response from createCheckout mutation:', data);
      
      // If the API doesn't work, fall back to UUID generation
      return {
        id: generateUUID()
      };
    }
  } catch (error) {
    console.error('Error creating checkout session:', error);
    
    // If the API call fails, fall back to UUID generation
    return {
      id: generateUUID()
    };
  }
}

/**
 * Adds an item to a Dutchie checkout using the GraphQL API
 * @param retailerId - The retailer ID
 * @param checkoutId - The checkout session ID
 * @param productId - The product ID
 * @param variantId - The variant ID
 * @param quantity - The quantity to add
 * @returns The updated checkout session
 */
async function addItemToDutchieCheckout(
  retailerId: string,
  checkoutId: string,
  productId: string,
  variantId: string,
  quantity: number
): Promise<any> {
  try {
    // Extract the option from the variant ID if it exists
    // In Dutchie API, the option is often included in the variant ID like "id~option"
    // The tilde character separates the actual ID from the option
    let option = "";
    if (variantId.includes("~")) {
      const parts = variantId.split("~");
      option = parts[1] || "";
    }

    // Create the GraphQL mutation
    const mutation = `
      mutation AddItemToCheckout(
        $retailerId: ID!,
        $checkoutId: ID!,
        $productId: ID!,
        $quantity: Int!,
        $option: String!
      ) {
        addItem(
          retailerId: $retailerId,
          checkoutId: $checkoutId,
          productId: $productId,
          quantity: $quantity,
          option: $option
        ) {
          id
          items {
            id
            quantity
          }
        }
      }`;

    // Make the GraphQL request
    const response = await fetch('https://dutchie.com/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        query: mutation,
        variables: {
          retailerId,
          checkoutId,
          productId,
          quantity,
          option
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to add item to checkout: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (data?.data?.addItem) {
      return data.data.addItem;
    } else {
      console.warn('Unexpected response from addItem mutation:', data);
      return null;
    }
  } catch (error) {
    console.error('Error adding item to checkout:', error);
    return null;
  }
}

/**
 * Gets an existing checkout ID from cookies or creates a new one if none exists
 * @returns A checkout ID string (UUID format)
 */
function getOrCreateCheckoutId(): string {
  if (typeof window === 'undefined') {
    // Server-side rendering, return a placeholder (will be replaced client-side)
    return generateUUID();
  }
  
  // Try to get existing checkout ID from cookies
  const cookies = document.cookie.split('; ');
  const checkoutCookie = cookies.find(cookie => cookie.startsWith('dutchie-checkout-id='));
  
  if (checkoutCookie) {
    const checkoutId = checkoutCookie.split('=')[1];
    if (checkoutId && checkoutId.length > 10) { // Basic validation
      return checkoutId;
    }
  }
  
  // No valid checkout ID found, create a new one
  const newCheckoutId = generateUUID();
  
  // Save the new checkout ID in a cookie with 30-day expiry
  document.cookie = `dutchie-checkout-id=${newCheckoutId}; path=/; domain=716cannabisllc.com; max-age=${30*24*60*60}`;
  
  return newCheckoutId;
}

/**
 * Generates a UUID v4 (random) string
 * Used for creating unique checkout session tokens
 * @returns A UUID v4 formatted string (xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx)
 */
function generateUUID(): string {
  // Use the browser's crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  
  // Fallback implementation for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Mock data for development
 * Export this function so it can be used by the API route
 */
export function getMockProducts(): DutchieProduct[] {
  return [
    {
      id: "p1",
      name: "OG Kush",
      category: "Flower",
      subcategory: "Flower",
      strainType: "hybrid",
      effects: ["Relaxed", "Happy", "Euphoric"],
      brand: {
        id: "b1",
        name: "Tyson 2.0",
      },
      variants: [
        {
          id: "v1",
          option: "3.5g",
          priceRec: 45,
          quantity: 10,
        },
        {
          id: "v2",
          option: "7g",
          priceRec: 80,
          quantity: 5,
        },
      ],
      image: "/images/products/Pearls-Daytime-Sativa.png",
      potencyThc: {
        formatted: "24% THC",
        range: [22, 26],
        unit: "%",
      },
      potencyCbd: {
        formatted: "0.1% CBD",
        range: [0, 0.2],
        unit: "%",
      },
      description:
        "OG Kush is a legendary strain with a complex aroma of fuel, skunk, and spice that delivers heavy euphoria and relaxation.",
      staffPick: true,
    },
    {
      id: "p2",
      name: "Blue Dream Vape",
      category: "Vaporizers",
      subcategory: "Cartridges",
      strainType: "hybrid",
      effects: ["Creative", "Uplifted", "Relaxed"],
      brand: {
        id: "b2",
        name: "Rove",
      },
      variants: [
        {
          id: "v3",
          option: "1g",
          priceRec: 60,
          quantity: 15,
        },
      ],
      image: "/images/products/jetpack.png",
      potencyThc: {
        formatted: "85% THC",
        range: [82, 88],
        unit: "%",
      },
      potencyCbd: {
        formatted: "0.5% CBD",
        range: [0, 1],
        unit: "%",
      },
      description: "A sativa-dominant hybrid vape cartridge offering sweet berry flavor and balanced effects.",
    },
    {
      id: "p3",
      name: "Sour Diesel",
      category: "Flower",
      subcategory: "Flower",
      strainType: "sativa",
      effects: ["Energetic", "Euphoric", "Creative"],
      brand: {
        id: "b3",
        name: "Electraleaf",
      },
      variants: [
        {
          id: "v4",
          option: "1g",
          priceRec: 15,
          quantity: 20,
        },
        {
          id: "v5",
          option: "3.5g",
          priceRec: 50,
          quantity: 8,
        },
      ],
      image: "/images/products/Pearls-Daytime-Sativa.png",
      potencyThc: {
        formatted: "22% THC",
        range: [20, 24],
        unit: "%",
      },
      potencyCbd: {
        formatted: "0.2% CBD",
        range: [0, 0.4],
        unit: "%",
      },
      description: "Known for its diesel-like aroma, Sour Diesel delivers energizing, dreamy cerebral effects.",
    },
  ]
}


